<div class="container py-4">

  <h2 class="mb-4 fw-bold">Rental Applications</h2>

  <ng-container *ngIf="applicationRounds$ | async as rounds; else loading">

    <ng-container *ngIf="rounds.length; else empty">

      <div *ngFor="let round of rounds" class="card shadow-sm mb-4">

        <!-- ACCORDION HEADER -->
        <div
          class="card-header d-flex justify-content-between align-items-center"
          style="cursor: pointer"
          (click)="toggleRound(round)"
        >
          <div>
            <div class="fw-semibold">
              {{ round.rental?.address }}
            </div>
            <div class="text-muted small">
              {{ round.rental?.price | currency }}/mo ·
              Round {{ round.roundNumber }} ·
              {{ round.applications.length }} applications
            </div>
          </div>

          <span
            class="badge"
            [class.bg-success]="round.status === 'open'"
            [class.bg-secondary]="round.status === 'closed'"
          >
            {{ round.status }}
          </span>
        </div>

        <!-- ACCORDION BODY -->
        <div *ngIf="round.expanded" class="card-body">

          <!-- SORT CONTROLS -->
          <div class="d-flex gap-2 mb-3">
            <button
              class="btn btn-sm"
              [class.btn-primary]="round.sortBy === 'income'"
              [class.btn-outline-primary]="round.sortBy !== 'income'"
              (click)="sortApplications(round, 'income')"
            >
              Sort by Income
            </button>

            <button
              class="btn btn-sm"
              [class.btn-primary]="round.sortBy === 'date'"
              [class.btn-outline-primary]="round.sortBy !== 'date'"
              (click)="sortApplications(round, 'date')"
            >
              Sort by Date
            </button>
          </div>

          <!-- MOBILE VIEW -->
          <div class="d-block d-md-none">
            <div
              *ngFor="let app of getPagedApplications(round)"
              class="border rounded p-3 mb-3"
              (click)="openApplicationDetails(round, app)"
            >
              <div class="fw-semibold">{{ app.fullName }}</div>

              <div class="row mt-2">
                <div class="col-6">
                  <small class="text-muted">Total Income</small>
                  <div class="fw-bold text-success">
                    ${{ getTotalIncome(app) | number }}
                  </div>
                </div>
                <div class="col-6">
                  <small class="text-muted">Current Rent</small>
                  <div class="fw-bold">
                    ${{ app.currentRent | number }}
                  </div>
                </div>
              </div>

              <div class="small text-muted mt-2">
                Submitted {{ getCreatedAtDate(app.createdAt) | date:'medium' }}
              </div>
            </div>
          </div>

          <!-- DESKTOP TABLE -->
          <div class="table-responsive d-none d-md-block">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th>Name</th>
                  <th>Total Income</th>
                  <th>Job Title</th>
                  <th>Occupants</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                <tr
                  *ngFor="let app of getPagedApplications(round)"
                  (click)="openApplicationDetails(round, app)"
                  style="cursor:pointer"
                >
                  <td>{{ app.fullName }}</td>
                  <td class="fw-bold text-success">
                    ${{ getTotalIncome(app) | number }}
                  </td>
                  <td>{{ app.jobTitle }}</td>
                  <td>{{ app.occupantsDescription || '—' }}</td>
                  <td>
                    {{ getCreatedAtDate(app.createdAt) | date:'medium' }}
                  </td>
                  <td class="text-end">
                    <span class="badge bg-secondary">
                      {{ app.status }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- PAGINATION -->
          <div
            class="d-flex justify-content-between align-items-center mt-3"
            *ngIf="totalPages(round) > 1"
          >
            <button
              class="btn btn-outline-secondary btn-sm"
              [disabled]="round.page === 1"
              (click)="round.page = round.page - 1"
            >
              Previous
            </button>

            <span class="small text-muted">
              Page {{ round.page }} of {{ totalPages(round) }}
            </span>

            <button
              class="btn btn-outline-secondary btn-sm"
              [disabled]="round.page === totalPages(round)"
              (click)="round.page = round.page + 1"
            >
              Next
            </button>
          </div>

        </div>
      </div>

    </ng-container>

  </ng-container>

  <ng-template #empty>
    <div class="alert alert-info">
      No active application rounds.
    </div>
  </ng-template>

  <ng-template #loading>
    <div class="text-center py-5 text-muted">
      Loading applications…
    </div>
  </ng-template>

</div>


<!-- APPLICATION DETAILS MODAL -->
<div
  *ngIf="selectedApplication"
  class="app-modal-overlay"
  (click)="closeModal()">

  <div
    class="app-modal"
    (click)="$event.stopPropagation()">

    <!-- HEADER -->
    <div class="app-modal-header">
      <h5 class="mb-0">
        {{ selectedApplication.fullName }}
      </h5>
      <button
        class="btn-close"
        style="float: right;justify-content: right;"
        (click)="closeModal()">
      </button>
    </div>

    <!-- BODY -->
<div class="app-modal-body">

  <!-- =========================
       APPLICANT INFO
       ========================= -->
  <section class="mb-4">
    <h6 class="section-title">Applicant Information</h6>
    <div class="row g-3">
      <div class="col-md-6"><span>Full Name</span><strong>{{ selectedApplication.fullName }}</strong></div>
      <div class="col-md-6"><span>Email</span><strong>{{ selectedApplication.applicantEmail }}</strong></div>
      <div class="col-md-6"><span>Phone</span><strong>{{ selectedApplication.phone }}</strong></div>
      <div class="col-md-6"><span>Date of Birth</span><strong>{{ selectedApplication.dob }}</strong></div>
      <div class="col-md-6"><span>SSN</span><strong>{{ selectedApplication.ssn }}</strong></div>
      <div class="col-md-6"><span>Driver’s License</span><strong>{{ selectedApplication.driversLicenseNum }}</strong></div>
    </div>
  </section>

  <!-- =========================
       CURRENT HOUSING
       ========================= -->
  <section class="mb-4">
    <h6 class="section-title">Current Housing</h6>
    <div class="row g-3">
      <div class="col-md-6"><span>Current Address</span><strong>{{ selectedApplication.currentAddress }}</strong></div>
      <div class="col-md-3"><span>Current Rent</span><strong>${{ selectedApplication.currentRent }}</strong></div>
      <div class="col-md-3"><span>How Long</span><strong>{{ selectedApplication.howLong }}</strong></div>

      <div class="col-md-4"><span>Landlord Name</span><strong>{{ selectedApplication.currentLandlordName || '—' }}</strong></div>
      <div class="col-md-4"><span>Landlord Phone</span><strong>{{ selectedApplication.currentLandlordPhone || '—' }}</strong></div>
      <div class="col-md-4"><span>Landlord Email</span><strong>{{ selectedApplication.currentLandlordEmail || '—' }}</strong></div>

      <div class="col-12"><span>Reason for Leaving</span><strong>{{ selectedApplication.currentReason }}</strong></div>
    </div>
  </section>

  <!-- =========================
       EMPLOYMENT
       ========================= -->
  <section class="mb-4">
    <h6 class="section-title">Employment</h6>
    <div class="row g-3">
      <div class="col-md-6"><span>Employer</span><strong>{{ selectedApplication.employerName }}</strong></div>
      <div class="col-md-6"><span>Employer Address</span><strong>{{ selectedApplication.employerAddress || '—' }}</strong></div>
      <div class="col-md-4"><span>Job Title</span><strong>{{ selectedApplication.jobTitle }}</strong></div>
      <div class="col-md-4"><span>Income</span><strong>${{ selectedApplication.income }}</strong></div>
      <div class="col-md-4"><span>How Long</span><strong>{{ selectedApplication.jobHowLong }}</strong></div>

      <div class="col-md-4"><span>Supervisor</span><strong>{{ selectedApplication.supervisorName }}</strong></div>
      <div class="col-md-4"><span>Supervisor Phone</span><strong>{{ selectedApplication.supervisorPhone }}</strong></div>
      <div class="col-md-4"><span>Supervisor Email</span><strong>{{ selectedApplication.supervisorEmail || '—' }}</strong></div>
    </div>
  </section>

  <!-- =========================
       CO-APPLICANT
       ========================= -->
  <section class="mb-4">
    <h6 class="section-title">Co-Applicant</h6>
    <div class="row g-3">
      <div class="col-md-6"><span>Full Name</span><strong>{{ selectedApplication.coAppFullName || '—' }}</strong></div>
      <div class="col-md-6"><span>Email</span><strong>{{ selectedApplication.coAppEmail || '—' }}</strong></div>
      <div class="col-md-6"><span>Phone</span><strong>{{ selectedApplication.coAppPhone || '—' }}</strong></div>
      <div class="col-md-4"><span>Job Title</span><strong>{{ selectedApplication.cojobTitle || '—' }}</strong></div>
      <div class="col-md-4"><span>Income</span><strong>{{ selectedApplication.coincome || '—' }}</strong></div>
      <div class="col-md-4"><span>How Long</span><strong>{{ selectedApplication.cohowLong || '—' }}</strong></div>
    </div>
  </section>

  <!-- =========================
       HOUSEHOLD
       ========================= -->
  <section class="mb-4">
    <h6 class="section-title">Household & Pets</h6>
    <div class="row g-3">
      <div class="col-md-6"><span>Occupants</span><strong>{{ selectedApplication.occupantsDescription }}</strong></div>
      <div class="col-md-6"><span>Additional Occupants</span><strong>{{ selectedApplication.additionalOccupants }}</strong></div>
      <div class="col-md-6"><span>Pets</span><strong>{{ selectedApplication.hasPets }}</strong></div>
      <div class="col-md-6"><span>Pet Details</span><strong>{{ selectedApplication.petsDescription || '—' }}</strong></div>
    </div>
  </section>

  <!-- =========================
       BACKGROUND
       ========================= -->
  <section class="mb-4">
    <h6 class="section-title">Background & Disclosures</h6>
    <div class="row g-3">
      <div class="col-md-3"><span>Evicted</span><strong>{{ selectedApplication.evicted }}</strong></div>
      <div class="col-md-3"><span>Eviction Details</span><strong>{{ selectedApplication.evictionDetails || '—' }}</strong></div>

      <div class="col-md-3"><span>Bankruptcy</span><strong>{{ selectedApplication.bankruptcy }}</strong></div>
      <div class="col-md-3"><span>Bankruptcy Details</span><strong>{{ selectedApplication.bankruptcyDetails || '—' }}</strong></div>

      <div class="col-md-3"><span>Convicted</span><strong>{{ selectedApplication.convicted }}</strong></div>
      <div class="col-md-3"><span>Crime Details</span><strong>{{ selectedApplication.crimeDetails || '—' }}</strong></div>

      <div class="col-md-3"><span>Lease Violation</span><strong>{{ selectedApplication.leaseViolation }}</strong></div>
      <div class="col-md-3"><span>Violation Details</span><strong>{{ selectedApplication.leaseViolationDetails || '—' }}</strong></div>

      <div class="col-md-3"><span>Landlord Balance Owed</span><strong>{{ selectedApplication.landlordBalance }}</strong></div>
      <div class="col-md-3"><span>Balance Details</span><strong>{{ selectedApplication.landlordBalanceDetails || '—' }}</strong></div>
    </div>
  </section>

  <!-- =========================
       VEHICLES
       ========================= -->
  <section class="mb-4">
    <h6 class="section-title">Vehicles</h6>
    <div class="row g-3">
      <div class="col-md-6">
        <span>Vehicle 1</span>
        <strong>
          {{ selectedApplication.vehicle1Year }}
          {{ selectedApplication.vehicle1Make }}
          {{ selectedApplication.vehicle1Model }}
          ({{ selectedApplication.vehicle1Plate }})
        </strong>
      </div>
      <div class="col-md-6">
        <span>Vehicle 2</span>
        <strong>
          {{ selectedApplication.vehicle2Year }}
          {{ selectedApplication.vehicle2Make }}
          {{ selectedApplication.vehicle2Model }}
          ({{ selectedApplication.vehicle2Plate || '—' }})
        </strong>
      </div>
    </div>
  </section>

</div>



    <!-- FOOTER -->
    <div class="app-modal-footer">
      <button class="btn btn-outline-secondary" (click)="closeModal()">Close</button>
      <button class="btn btn-danger" (click)="rejectApplication(selectedRound.id,selectedApplication)">Reject</button>
      <button class="btn btn-success" (click)="approveApplication(selectedRound.id, selectedRound.rentalId, selectedApplication)">Approved</button>
      <button
        class="btn btn-outline-dark btn-sm"
        (click)="printApplication(selectedRound.id, selectedApplication.id)">
        Print Application
      </button>


    </div>

  </div>
</div>